import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Box, Paper, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MuiAutocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";

const SearchableAutocompleteContext = createContext(null);

function flattenText(value, depth = 0) {
  if (value === null || value === undefined || typeof value === "boolean") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (depth > 2) return "";
  if (Array.isArray(value)) return value.map((item) => flattenText(item, depth + 1)).join(" ");
  if (typeof value === "object") {
    return Object.values(value)
      .map((item) => flattenText(item, depth + 1))
      .filter(Boolean)
      .join(" ");
  }
  return "";
}

function SearchableAutocompletePaper(paperProps) {
  const context = useContext(SearchableAutocompleteContext);
  const BasePaper = context?.PaperComponent || Paper;
  const { children, ...restPaperProps } = paperProps;

  if (!context) {
    return <BasePaper {...restPaperProps}>{children}</BasePaper>;
  }

  const {
    handleSearchChange,
    markSearchInteraction,
    searchInputRef,
    searchPlaceholder,
    searchText,
  } = context;

  return (
    <BasePaper {...restPaperProps}>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          p: 1,
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
        onMouseDown={(event) => {
          markSearchInteraction();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          searchInputRef.current?.focus();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Escape") event.stopPropagation();
        }}
      >
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          value={searchText}
          placeholder={searchPlaceholder}
          onChange={handleSearchChange}
          onFocus={markSearchInteraction}
          onMouseDown={(event) => {
            markSearchInteraction();
            event.stopPropagation();
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key !== "Escape") event.stopPropagation();
          }}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, opacity: 0.65 }} />,
          }}
        />
      </Box>
      {children}
    </BasePaper>
  );
}

export default function SearchableAutocomplete({
  filterOptions,
  getOptionLabel,
  onClose,
  onOpen,
  open: openProp,
  options = [],
  openOnFocus = true,
  autoHighlight = true,
  PaperComponent,
  searchPlaceholder,
  ...props
}) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const searchInputRef = useRef(null);
  const keepOpenForSearchRef = useRef(false);
  const isOpenControlled = openProp !== undefined;
  const open = isOpenControlled ? openProp : internalOpen;

  const fallbackFilter = useMemo(
    () =>
      createFilterOptions({
        ignoreAccents: true,
        ignoreCase: true,
        stringify: (option) => {
          const label = typeof getOptionLabel === "function" ? getOptionLabel(option) : "";
          return `${label} ${flattenText(option)}`;
        },
      }),
    [getOptionLabel]
  );

  const filteredOptions = useMemo(
    () =>
      (filterOptions || fallbackFilter)(options, {
        inputValue: searchText,
        getOptionLabel: getOptionLabel || ((option) => option?.label ?? String(option ?? "")),
      }),
    [fallbackFilter, filterOptions, getOptionLabel, options, searchText]
  );

  const handleSearchChange = useCallback(
    (event) => {
      setSearchText(event.target.value);
    },
    []
  );

  const handleOpen = useCallback(
    (event) => {
      setSearchText("");
      if (!isOpenControlled) setInternalOpen(true);
      onOpen?.(event);
    },
    [isOpenControlled, onOpen]
  );

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "blur" && keepOpenForSearchRef.current) return;
      setSearchText("");
      if (!isOpenControlled) setInternalOpen(false);
      onClose?.(event, reason);
    },
    [isOpenControlled, onClose]
  );

  const markSearchInteraction = useCallback(() => {
    keepOpenForSearchRef.current = true;
    window.setTimeout(() => {
      keepOpenForSearchRef.current = false;
    }, 200);
  }, []);

  const DropdownPaper = useMemo(() => {
    return {
      handleSearchChange,
      markSearchInteraction,
      PaperComponent,
      searchInputRef,
      searchPlaceholder: searchPlaceholder || t("search", { defaultValue: "Search" }),
      searchText,
    };
  }, [PaperComponent, handleSearchChange, markSearchInteraction, searchPlaceholder, searchText, t]);

  return (
    <SearchableAutocompleteContext.Provider value={DropdownPaper}>
      <MuiAutocomplete
        {...props}
        getOptionLabel={getOptionLabel}
        options={filteredOptions}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        openOnFocus={openOnFocus}
        autoHighlight={autoHighlight}
        filterOptions={(items) => items}
        PaperComponent={SearchableAutocompletePaper}
      />
    </SearchableAutocompleteContext.Provider>
  );
}
